import React, {ReactNode} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import {useNotifications} from "context/NotificationContext";
import {useOperator} from "context/OperatorContext";
import {useAuth} from "context/AuthContext";
import ngage from "services/ngage";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import TrailForm from "pages/Trail/Form";
import {TrailAPIData, TrailFormData} from "types/Trail";
import Button from "components/Button";
import {EyeIcon} from "@heroicons/react/solid";
import NotAuthorised from "pages/NotAuthorised";
import {useSubmitActionRef} from "../../../hooks/useSubmitActionRef";
import { TrailWaypointAPIData } from "../../../types/TrailWaypoint";

function MutateTrail() {
    const {termFor} = useOperator();
    const auth = useAuth();
    const {notify} = useNotifications();
    const navigate = useNavigate();
    const {id} = useParams();

    const {data: trail, isLoading} = useQuery(
        ['trails', {id}],
        () => ngage.trails.get(Number(id)),
        {enabled: !!id && auth.can('trails:read')}
    )

    const store = useMutation((data: TrailAPIData) => ngage.trails.store(data));
    const update = useMutation((data: TrailAPIData) => ngage.trails.update(Number(id), data));

    const submitActionRef = useSubmitActionRef();

    // Handle form submission
    async function handleSubmit(values: TrailFormData, helpers: FormikHelpers<TrailFormData>) {

        const waypoints: TrailWaypointAPIData[] = values.waypoints.map(waypoint => {
            const exhibit_ids = waypoint.exhibits.map(exhibit => exhibit.id);
            const facility_ids = waypoint.facilities.map(facility => facility.id);
            const {exhibits, facilities, ...waypointData} = waypoint;

            return {
                ...waypointData,
                exhibit_ids,
                facility_ids,
            }
        });

        const newValues = {
            ...values,
            waypoints,
        } as TrailAPIData

        const trailData = id? await handleUpdate(newValues) : await handleStore(newValues)
        await invalidate('trails.all');

        helpers.setSubmitting(false)

        if (submitActionRef.current === 'save') {
          const trailId = trailData?.id;

          if (trailId) navigate(`/trails/${trailId}/edit`, {replace: true});
        } else {
          navigate(`/trails`);
        }
    }

    // Handle store mutation
    async function handleStore(values: TrailAPIData) {
        try {
            const trail = await store.mutateAsync(values)
            notify("success", `${termFor('Trail')} Created`, `${values.name} has been created`);
            return trail;
        } catch (error: any) {
            notify("error", `${termFor('Trail')} Not Created`, `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: TrailAPIData) {
        try {
            const trail = await update.mutateAsync(values)
            notify("success", `${termFor('Trail')} Updated`, `${values.name} has been updated`)
            await invalidate(['trails', {id}]);
            return trail;
        } catch (error: any) {
            notify("error", `${termFor('Trail')} Not Updated`, `${values.name} could not be updated`)
            throw error
        }
    }

    // Handle view action
    function handleView() {
        if(id) navigate(`/trails/${id}`)
    }

    // Prevent unauthorised viewing
    if (auth.cannot('trails:write')) {
        return <NotAuthorised/>
    }

    // Prepare page props
    let title: string
    let breadcrumbs: Record<string, string>
    let actions: ReactNode

    if(id) {
        title = `Edit ${termFor('Trail')}`

        breadcrumbs = {
            'App Content': '/',
            'Site': '/',
            [termFor('Trails')]: '/trails',
            [`Edit ${termFor('Trail')}`]: `/trails/${id}/edit`
        }

        actions = (
          <>
              {auth.can('trails:read') &&
                <Button color={Button.colors.PRIMARY} icon={EyeIcon} onClick={handleView}>View</Button>
              }
          </>
        )
    } else {
        title = `New ${termFor('Trail')}`

        breadcrumbs = {
            'App Content': '/',
            'Site': '/',
            [termFor('Trails')]: '/trails',
            [`New ${termFor('Trail')}`]: '/trails/new'
        }

        actions = null
    }

    return (
        <Page title={title} breadcrumbs={breadcrumbs} actions={actions} isLoading={isLoading}>
            <TrailForm trail={trail} onSubmit={handleSubmit} submitActionRef={submitActionRef}/>
        </Page>
    );
}

export default MutateTrail;